import React, { useEffect, useState } from 'react';
import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import { Image, Space, Tag } from 'antd';
import { ChatAttachment } from '../core/support-widget-core';

const IMAGE_MIME_PREFIX = 'image/';

export interface AttachmentGalleryProps {
    attachments: ChatAttachment[];
    downloadLabel: string;
    getAttachmentDownloadUrl: (
        attachmentId: number,
    ) => Promise<{ ok: true; downloadUrl: string } | { ok: false; error: string }>;
}

/**
 * Inline image thumbnails and download chips for message attachments (CHAT-06, D-32).
 */
export const AttachmentGallery: React.FC<AttachmentGalleryProps> = ({
    attachments,
    downloadLabel,
    getAttachmentDownloadUrl,
}) => {
    const [urls, setUrls] = useState<Record<number, string>>({});

    useEffect(() => {
        let cancelled = false;
        for (const attachment of attachments) {
            if (!attachment.mimeType.startsWith(IMAGE_MIME_PREFIX)) {
                continue;
            }
            void getAttachmentDownloadUrl(attachment.id).then((result) => {
                if (cancelled || !result.ok) {
                    return;
                }
                setUrls((prev) => ({ ...prev, [attachment.id]: result.downloadUrl }));
            });
        }
        return () => {
            cancelled = true;
        };
    }, [attachments, getAttachmentDownloadUrl]);

    const handleDownload = async (attachmentId: number) => {
        const result = await getAttachmentDownloadUrl(attachmentId);
        if (result.ok) {
            window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <Space wrap size="small">
            {attachments.map((attachment) => {
                const isImage = attachment.mimeType.startsWith(IMAGE_MIME_PREFIX);
                if (isImage) {
                    return (
                        <Image
                            key={attachment.id}
                            src={urls[attachment.id]}
                            alt={attachment.filename}
                            width={120}
                            height={120}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                            placeholder
                        />
                    );
                }
                return (
                    <Tag
                        key={attachment.id}
                        icon={<FileOutlined />}
                        style={{ cursor: 'pointer', padding: '4px 8px' }}
                        onClick={() => void handleDownload(attachment.id)}
                        title={downloadLabel}
                    >
                        {attachment.filename} <DownloadOutlined />
                    </Tag>
                );
            })}
        </Space>
    );
};
